/**
 * Signature re-capture — document + signature screen (PF-6119)
 *
 * Screen 2 of `/signature-recapture/:recaptureId`. Shows the actual Abtretungserklärung the patient
 * is re-signing, and gates the submit button on a live ink check. The server re-checks on submit and
 * stays authoritative.
 */
import type { SignatureInkThresholds } from '@physiofit-digital/physiofit_types';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { space } from '../../../styles/onboardingUiTokens';
import { submitSignatureRecapture } from '../api/submitSignatureRecapture';
import { DocumentPreview } from '../components/DocumentPreview';
import { SignaturePadField, type SignatureInkStatus } from '../components/SignaturePadField';
import { signatureRecaptureCopy } from '../config/copy';
import { Body, H1, Notice, OnboardingBody, OnboardingFooter, OnboardingHeader, OnboardingLayout } from '../ui';

const copy = signatureRecaptureCopy.sign;

export type SignScreenProps = {
  documentUrl: string;
  documentName: string;
  sessionToken: string;
  thresholds: SignatureInkThresholds;
  onBack: () => void;
  onSubmitted: () => void;
};

export function SignScreen({
  documentUrl,
  documentName,
  sessionToken,
  thresholds,
  onBack,
  onSubmitted,
}: SignScreenProps) {
  const [signature, setSignature] = useState<string | null>(null);
  const [inkStatus, setInkStatus] = useState<SignatureInkStatus | null>(null);
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const handleSignatureChange = useCallback((value: string | null, status: SignatureInkStatus | null) => {
    setSignature(value);
    setInkStatus(status);
    setSubmitError(undefined);
  }, []);

  // The button waits for an accept; the copy only appears on a settled reject. Keeping the two apart
  // is what stops the notice flashing on every finger lift.
  const inkError = inkStatus === 'rejected' ? copy.inkError : undefined;

  const handleSubmit = useCallback(async () => {
    if (!signature) {
      return;
    }
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const result = await submitSignatureRecapture({ sessionToken, signatureBase64: signature });
      if (result.status === 'rejected') {
        // The client check already gates the button, so reaching this means the browser and `sharp`
        // measurements disagree. Show the same ink error rather than a generic failure, and keep the
        // signature on the pad so the patient can extend it instead of starting over.
        setInkStatus('rejected');
        return;
      }
      onSubmitted();
    } catch {
      setSubmitError(copy.submitError);
    } finally {
      setSubmitting(false);
    }
  }, [signature, sessionToken, onSubmitted]);

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={3}
      onBack={onBack}
      onForward={handleSubmit}
      forwardLabel={copy.forwardLabel}
      forwardDisabled={!signature || inkStatus !== 'accepted' || submitting}
      forwardBusy={submitting}
    >
      <OnboardingHeader />
      <OnboardingBody disableSupportHint>
        <ContentStack>
          <H1>{copy.title}</H1>
          <Body variant="document">{copy.body}</Body>
          <DocumentPreview url={documentUrl} name={documentName} />
          <SignaturePadField
            value={signature}
            thresholds={thresholds}
            error={inkError}
            onChange={handleSignatureChange}
          />
          {submitError ? <Notice preset="error">{submitError}</Notice> : null}
        </ContentStack>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;
